{
  "layout": {
    "notFound": {
      "title": "Competition not found",
      "description": "It does not exist, or you hold no role in it. The two are answered identically on purpose."
    },
    "accessDenied": {
      "title": "You can't access this competition's admin workspace",
      "description": "You are not currently able to enter this competition in this role.",
      "back": "Go to a page you can use"
    },
    "loading": "Loading…",
    "pinned": "pinned by an administrator",
    "adminGuide": "Admin guide",
    "sections": {
      "overview": "Overview",
      "categories": "Categories",
      "rounds": "Rounds",
      "forms": "Forms",
      "teams": "Teams",
      "people": "People & roles",
      "audit": "Audit log",
      "submissions": "Submissions",
      "judges": "Judges",
      "judgingLoad": "Review Progress",
      "rubrics": "Rubrics",
      "results": "Results",
      "files": "Files",
      "participants": "Participants",
      "registrationData": "Registration Data",
      "eligibilityData": "Eligibility Data",
      "exports": "Export Center",
      "operationsDashboard": "Operations dashboard",
      "eligibilityReviews": "Eligibility Review",
      "announcements": "Announcements",
      "manualScores": "Manual entry"
    },
    "navGroups": {
      "settings": "Settings",
      "operations": "Operations",
      "data": "Data",
      "review": "Review",
      "permissions": "Permissions",
      "governance": "Governance"
    }
  },
  "operations": {
    "title": "Operations Dashboard",
    "description": "How the competition is running right now: registration, eligibility, submissions, judging progress.",
    "loading": "Loading…",
    "loadFailed": "Could not load operations data.",
    "retry": "Retry",
    "refresh": "Refresh",
    "notApplicable": "N/A",
    "updatedAt": "Last updated: {{time}}",
    "stage": {
      "registration": "Registration",
      "submission": "Submission",
      "judging": "Judging",
      "results": "Results"
    },
    "settingsCta": "Go to Settings Overview →",
    "groups": {
      "registration": "Registration",
      "participants": "Participants",
      "eligibility": "Eligibility",
      "submission": "Submission",
      "judging": "Judging"
    },
    "kpi": {
      "registration": {
        "teams": "Registered teams",
        "complete": "Completed registration",
        "incomplete": "Incomplete registration",
        "cancelled": "Cancelled / withdrawn"
      },
      "participants": {
        "total": "Total participants"
      },
      "eligibility": {
        "required": "Members requiring verification",
        "submitted": "Proof submitted",
        "pending": "Pending review",
        "verified": "Verified",
        "rejected": "Rejected",
        "missing": "Not submitted",
        "expired": "Expired",
        "submissionRate": "Proof submission rate",
        "approvalRate": "Verification approval rate"
      },
      "submission": {
        "required": "Submissions required",
        "submitted": "Submitted",
        "draft": "Draft",
        "missing": "Not submitted"
      },
      "judging": {
        "judges": "Total judges",
        "accepted": "Accepted invite",
        "pendingInvite": "Pending invite",
        "assignments": "Judging assignments",
        "completedAssignments": "Completed",
        "completionRate": "Judging completion rate"
      }
    },
    "needsAttention": {
      "title": "Needs Attention",
      "empty": "Nothing needs immediate attention right now.",
      "severity": {
        "critical": "Critical",
        "warning": "Warning",
        "info": "Info"
      },
      "items": {
        "eligibilityPending": "{{count}} student eligibility proofs pending review",
        "eligibilityMissing": "{{count}} required members have not submitted eligibility proof",
        "eligibilityRejected": "{{count}} student eligibility proofs were rejected, awaiting re-upload",
        "eligibilityExpired": "{{count}} members have an expired eligibility proof",
        "registrationIncomplete": "{{count}} teams have not completed registration",
        "submissionMissing": "{{count}} teams have not submitted their work",
        "judgingPendingInvite": "{{count}} judges have not accepted their invite"
      }
    },
    "hiddenGroups": {
      "hide": "Hide",
      "label": "Hidden:",
      "show": "Show"
    }
  },
  "list": {
    "title": "Competitions",
    "description": "The competitions you take part in. A super admin sees every competition on the platform.",
    "new": "New competition",
    "newFromTemplate": "Create from template",
    "newFromImport": "Import from JSON",
    "loading": "Loading…",
    "loadError": "Could not load competitions.",
    "empty": {
      "title": "Nothing here yet",
      "description": "You do not hold a role in any competition. An administrator has to grant you one."
    },
    "platformAdmin": "platform admin",
    "cta": {
      "admin": "Manage competition →",
      "participant": "Enter competition →",
      "viewOnly": "View competition →"
    },
    "filter": {
      "searchLabel": "Search by name",
      "searchPlaceholder": "Search competitions…",
      "statusLabel": "Status",
      "statusAll": "All statuses",
      "roleLabel": "My role",
      "roleAll": "All roles",
      "noMatches": "No competitions match the current filters.",
      "resultCount": "{{count}} competitions",
      "clear": "Clear filters"
    }
  },
  "categories": {
    "title": "Categories",
    "description": "Divisions within this competition, for example 高中組 and 大專組. Names are unique within a competition only.",
    "loading": "Loading…",
    "empty": "No categories yet.",
    "archived": "archived",
    "unlimitedTeams": "Unlimited teams",
    "upToTeams": "Up to {{count}} teams",
    "archive": "Archive",
    "add": {
      "title": "Add a category",
      "description": "Categories are archived rather than deleted, so teams that referenced one stay resolvable.",
      "name": "Name",
      "capacity": "Capacity (optional)",
      "failed": "Could not add the category.",
      "submit": "Add category"
    }
  },
  "rounds": {
    "title": "Rounds",
    "description": "A competition runs as one round by default. Add a final round to run a two-stage preliminary-then-final format.",
    "loading": "Loading…",
    "roundOrder": "Round {{order}}",
    "name": "Name",
    "status": "Status",
    "none": "None",
    "submissionForm": "Submission form",
    "defaultRubric": "Default rubric",
    "save": "Save round",
    "saving": "Saving…",
    "saved": "Round saved.",
    "saveFailed": "Could not save the round.",
    "statusBackwardWarning": "You are moving this round's status backward, from \"{{from}}\" to \"{{to}}\". This may affect whether registration, submission, or judging windows are considered open. Please confirm this is intentional.",
    "statusBackwardConfirm": "Confirm rollback and save",
    "key": {
      "preliminary": "Preliminary",
      "final": "Final"
    },
    "statusValue": {
      "draft": "Draft",
      "open": "Open",
      "judging": "Judging",
      "closed": "Closed"
    },
    "schedule": {
      "registrationStart": "Registration opens",
      "registrationEnd": "Registration closes",
      "submissionStart": "Submissions open",
      "submissionEnd": "Submissions close",
      "judgingStart": "Judging opens",
      "judgingEnd": "Judging closes",
      "resultPublish": "Results publish"
    },
    "add": {
      "title": "Add a round",
      "description": "A competition may have at most two rounds: a preliminary round and a final round.",
      "key": "Round type",
      "name": "Name",
      "failed": "Could not add the round.",
      "submit": "Add round"
    },
    "manual": {
      "modeLabel": "Scoring input",
      "mode": {
        "judge_only": "Judges only",
        "staff_only": "Staff input only",
        "judge_and_staff": "Judges plus staff input"
      },
      "modeHelp": {
        "judge_only": "Only judges score, using the rubric. This is today's calculation (default).",
        "staff_only": "Judges do not score; the result comes entirely from fields staff enter.",
        "judge_and_staff": "Starts from the judges' average, then applies staff-entered additions and deductions."
      },
      "fieldsTitle": "Manual input fields",
      "fieldsHelp": "Define the fields staff fill in, their score range and how they count. A field that already has entries can only be renamed.",
      "fieldsEmpty": "No fields yet.",
      "range": "range {{min}} to {{max}}",
      "effect": {
        "add": "Add",
        "deduct": "Deduct",
        "record_only": "Record only"
      },
      "effectHelp": {
        "add": "The value is added to the total.",
        "deduct": "The value (a positive number) is subtracted from the total.",
        "record_only": "Kept on record; does not change the total."
      },
      "deleteField": "Delete",
      "deleteFailed": "Could not delete the field.",
      "fieldName": "Field name",
      "minScore": "Minimum",
      "maxScore": "Maximum",
      "effectLabel": "How it counts",
      "rangeInvalid": "Enter a valid range; the maximum cannot be below the minimum.",
      "addField": "Add field",
      "addFailed": "Could not add the field."
    }
  },
  "forms": {
    "title": "Forms",
    "description": "Editing a form after it has been published clones a new draft version rather than changing what is currently live.",
    "createFailed": "Could not create the form.",
    "loading": "Loading…",
    "notCreated": "Not created yet.",
    "unpublished": "unpublished",
    "live": "v{{version}} live",
    "addOne": "Add a {{type}}",
    "singletonActiveExists": "This competition already has an active {{type}}. Archive it before adding another.",
    "namePrompt": "Name this form (e.g. Preliminary registration form) so it's recognizable when picking it for a round",
    "namePlaceholder": "Form name",
    "createSubmit": "Create",
    "createCancel": "Cancel",
    "rename": "Rename",
    "renameSave": "Save",
    "renameCancel": "Cancel",
    "renameFailed": "Could not update the form name.",
    "summary": {
      "fields": "{{count}} fields",
      "required": "{{count}} required",
      "fileFields": "{{count}} file field(s)",
      "unboundRound": "not bound to a round"
    }
  },
  "judges": {
    "autoDistribute": {
      "title": "Automatic balanced assignment",
      "description": "Proposes an even set of assignments for submitted works, based on each judge's current workload and category, skipping conflicts and existing assignments. You preview first; nothing is created until you confirm.",
      "round": "Round",
      "rubric": "Rubric",
      "perSubmission": "Judges per submission",
      "preview": "Preview assignment",
      "summary": "{{planned}} assignments planned, covering {{submissions}} submissions",
      "judge": "Judge",
      "load": "Workload (now + new)",
      "loadChange": "{{before}} + {{added}}",
      "underCovered": "{{count}} submission(s) cannot reach the target number of judges (conflicts or too few judges). Invite more judges or assign them by hand.",
      "nothing": "There is nothing to add.",
      "apply": "Apply assignment",
      "error": "Could not compute the assignment; check the round and rubric.",
      "dialogTitle": "Create the automatic assignments",
      "dialogTarget": "{{count}} assignments",
      "dialogScope": "Creates the previewed assignments, one audit entry each; conflicts and duplicates are checked again before creating.",
      "dialogImpact": "Judges receive one combined new-assignment email a few minutes later; you can adjust or cancel each assignment afterwards.",
      "dialogConfirm": "Confirm and create",
      "dialogSuccess": "{{created}} assignments created, {{skipped}} skipped because of a conflict or duplicate."
    },
    "title": "Judges",
    "description": "Manage judges, invitations, and submission assignments; grant the judge role in People & roles first.",
    "loading": "Loading…",
    "workload_one": "Workload: {{count}} active assignment",
    "workload_other": "Workload: {{count}} active assignments",
    "workloadBadge_one": "{{count}} active",
    "workloadBadge_other": "{{count}} active",
    "inviteCta": "+ Invite a judge",
    "summary": {
      "judges": "{{count}} judges",
      "pendingInvitations": "{{count}} pending invitations",
      "activeAssignments": "{{count}} active assignments",
      "unassignedSubmissions": "{{count}} unassigned submissions",
      "conflicts": "{{count}} declared conflicts"
    },
    "tabs": {
      "list": "Judges",
      "invite": "Invitations",
      "assign": "Assignments",
      "conflicts": "Conflicts"
    },
    "list": {
      "searchLabel": "Search judges",
      "searchPlaceholder": "Search by name, email, or expertise…",
      "filter": {
        "all": "All",
        "has": "Has workload",
        "none": "No workload"
      },
      "empty": "No judges yet.",
      "emptyTitle": "No judges invited yet",
      "emptyDescription": "Add or invite a judge before assigning submissions.",
      "noMatches": "No judges match the current filters.",
      "noExpertise": "—",
      "columns": {
        "name": "Judge",
        "email": "Email",
        "expertise": "Expertise",
        "workload": "Workload"
      }
    },
    "invite": {
      "title": "Invite a judge",
      "description": "Anyone without an account is created automatically and emailed a one-time sign-in; anyone who already has an account keeps their existing password and gets no new one. The admin never sees the password either way.",
      "emailPlaceholder": "Judge email",
      "submit": "Send invitation",
      "sent": "Invitation sent.",
      "failed": "Could not send the invitation.",
      "listTitle": "Invitation history",
      "listEmpty": "No invitations yet.",
      "columns": {
        "email": "Email",
        "status": "Status",
        "expiresAt": "Expires",
        "actions": "Actions"
      },
      "declineReason": "Reason: {{reason}}",
      "status": {
        "pending": "Pending",
        "declined": "Declined",
        "accepted": "Accepted",
        "cancelled": "Cancelled",
        "expired": "Expired"
      },
      "resend": "Resend",
      "resent": "Resent.",
      "resendFailed": "Could not resend.",
      "cancel": "Cancel invitation",
      "cancelled": "Invitation cancelled.",
      "cancelFailed": "Could not cancel the invitation."
    },
    "assign": {
      "title": "Manually assign judges",
      "description": "Check one or more submissions and one or more judges, scored against one rubric version. Every submission × judge pair is created; conflicts of interest and duplicates are skipped automatically with a reason shown.",
      "submission": "Submissions (multi-select)",
      "submissionPlaceholder": "Select a submission…",
      "searchSubmissions": "Search submissions",
      "searchSubmissionsPlaceholder": "Search by team or category…",
      "selectedSubmissions": "{{count}} selected",
      "noSubmissions": "No submitted entries are available to assign yet.",
      "judge": "Judges (multi-select)",
      "judgePlaceholder": "Select a judge…",
      "searchJudges": "Search judges",
      "searchJudgesPlaceholder": "Search by name or email…",
      "selectedJudges": "{{count}} selected",
      "judgeWorkload_one": "{{count}} active",
      "judgeWorkload_other": "{{count}} active",
      "noJudges": "No judges hold the judge role in this competition yet.",
      "noMatches": "No items match the current search.",
      "rubric": "Rubric",
      "rubricPlaceholder": "Select a rubric…",
      "noRubrics": "No rubric has been configured yet.",
      "submit": "Create assignments",
      "created": "Assignment created.",
      "failed": "Could not create the assignment.",
      "willCreate": "{{submissions}} submission(s) × {{judges}} judge(s) = {{total}} assignment(s) will be created",
      "batchResult": "Created {{created}} assignment(s), skipped {{skipped}}.",
      "skipReason": {
        "duplicate": "duplicate",
        "conflict_of_interest": "conflict of interest",
        "not_a_judge": "not a judge",
        "team_disqualified": "team disqualified"
      }
    },
    "assignments": {
      "title": "Current assignments",
      "description": "Every judge↔submission assignment in this competition.",
      "loading": "Loading…",
      "empty": "No assignments yet.",
      "searchLabel": "Search assignments",
      "searchPlaceholder": "Search by team, category, or judge…",
      "noMatches": "No assignments match the current search.",
      "columns": {
        "team": "Team",
        "category": "Category",
        "round": "Round",
        "judge": "Judge",
        "rubric": "Rubric version",
        "status": "Status",
        "createdAt": "Assigned",
        "actions": "Actions"
      },
      "judgeLabel": "Judge",
      "rubricLabel": "Rubric",
      "reassignConfirmTitle": "Confirm judge reassignment",
      "reassignImpact": "This submission will now be scored by \"{{to}}\" instead of \"{{from}}\", who will no longer score it.",
      "reassignSuccess": "Assignment updated.",
      "edit": "Edit",
      "cancel": "Cancel",
      "save": "Save",
      "saved": "Assignment updated.",
      "editFailed": "Could not update the assignment; it may duplicate another, or conflict.",
      "editOnlyActive": "Only an active assignment may be edited.",
      "teamDisqualified": "Disqualified",
      "disqualifiedBanner": "{{count}} active assignment(s) belong to disqualified teams; their scores will not be counted.",
      "cancelDisqualified": {
        "action": "Cancel assignments of disqualified teams",
        "dialogTitle": "Cancel assignments of disqualified teams",
        "dialogTarget": "{{count}} active assignment(s)",
        "dialogScope": "Every unsubmitted judge assignment on work from disqualified teams. Submitted scores and their records are not affected.",
        "dialogImpact": "Judges receive one combined \"assignments adjusted\" notice (without a reason); the work can be reassigned later.",
        "dialogConfirm": "Cancel these assignments",
        "dialogSuccess": "Those assignments were cancelled."
      }
    },
    "conflicts": {
      "empty": "No conflicts of interest have been declared for this competition.",
      "error": "Failed to load conflicts of interest.",
      "columns": {
        "judge": "Judge",
        "team": "Team",
        "reason": "Reason",
        "source": "Source",
        "declaredAt": "Declared at"
      },
      "source": {
        "self_declared": "Self-declared",
        "admin_defined": "Set by organizer"
      }
    }
  },
  "judgingLoad": {
    "reminders": {
      "title": "Remind judges to finish scoring",
      "auto": {
        "toggle": "Automatically remind judges before the deadline",
        "help": "When on, the system emails every judge with unsubmitted scores once per round, the chosen number of hours before that round's judging deadline. Off by default.",
        "hours": "Hours before",
        "save": "Save automatic reminder",
        "saved": "Automatic reminder saved.",
        "error": "Could not save; hours must be between 1 and 336.",
        "sent": "Automatic reminders already sent for {{count}} round(s)."
      },
      "description": "{{count}} judges still have scoring to finish.",
      "none": "No judge has pending scoring work.",
      "progress": "{{pending}} of {{total}} unfinished",
      "cta": "Send a reminder to unfinished judges",
      "dialogTitle": "Send judging reminder",
      "dialogTarget": "{{count}} judges",
      "dialogScope": "Sent to every judge with unsubmitted scores, listing how many are left, the nearest judging deadline, and a link to their assignments.",
      "dialogImpact": "The system sends the emails and records one audit entry; no scores or assignments change. Assignments with a declared conflict are not counted.",
      "dialogConfirm": "Send reminder",
      "dialogSuccess": "{{count}} reminders sent.",
      "previewTitle": "Email preview (sampled from the first judge)"
    },
    "title": "Review Progress",
    "description": "Per-judge assignment load, completion progress, and conflict-of-interest overlaps, for spotting issues before they slow judging down.",
    "loading": "Loading…",
    "table": {
      "title": "Judge table",
      "description": "Assignment counts, completion, and declared conflicts per judge.",
      "error": "Could not load the judging load table.",
      "empty": "No judges in this competition yet.",
      "noActivityYet": "No activity yet",
      "heuristicNote": "Overloaded means more than {{multiplier}}x the competition's average active-assignment load; No activity means at least one active assignment with none completed and no score update in the last {{days}} days. These are operational hints, not a judgment of any individual judge.",
      "columns": {
        "judge": "Judge",
        "status": "Status",
        "assigned": "Assigned",
        "completed": "Completed",
        "pending": "Pending",
        "coi": "COI",
        "lastActivity": "Last activity",
        "flags": "Flags"
      }
    },
    "flags": {
      "overloaded": "Overloaded",
      "under_assigned": "Under-assigned",
      "no_activity": "No activity"
    },
    "coi": {
      "title": "Conflict-of-interest overlaps",
      "description": "A judge with a declared conflict who still holds an active assignment for the same submission or team — a conflict should already have excluded the assignment, so this needs a look.",
      "error": "Could not load conflicts.",
      "empty": "No conflict-related assignment overlaps.",
      "columns": {
        "actions": "Actions"
      },
      "reassignCta": "Reassign",
      "cancelOnlyCta": "Cancel only",
      "selectJudgeLabel": "Select a new judge",
      "selectJudgePlaceholder": "Choose a judge...",
      "reassignSubmit": "Confirm reassignment",
      "reassignFailed": "Could not reassign — check that the new judge has not declared a conflict on this submission.",
      "confirmPrompt": "This cancels the judge's active assignment for this submission. The submission will then show as needing reassignment.",
      "confirmCta": "Cancel assignment",
      "cancelCta": "Never mind",
      "cancelReason": "Conflict of interest overlap resolved for {{judge}} via the Review Progress page.",
      "adjustFailed": "Could not cancel the assignment."
    }
  },
  "savedViews": {
    "selectLabel": "Saved views",
    "selectPlaceholder": "Choose a saved view...",
    "apply": "Apply",
    "delete": "Delete",
    "saveCurrentCta": "Save current filters as a view",
    "nameLabel": "View name",
    "namePlaceholder": "View name...",
    "saveSubmit": "Save",
    "saveCancel": "Cancel"
  },
  "teams": {
    "title": "Teams",
    "description": "View every team and member in this competition, remove a member, transfer team leadership, or create an invite link to manually add a member. Every action is written to the audit log.",
    "searchPlaceholder": "Search team name",
    "loading": "Loading…",
    "forbidden": "You do not have permission to view this competition's teams.",
    "empty": "There are no teams yet.",
    "filterEmpty": "No teams match the current filters.",
    "filters": {
      "statusPlaceholder": "Registration status",
      "statusAll": "All",
      "statusComplete": "Registration complete",
      "statusIncomplete": "Registration incomplete",
      "statusCancelled": "Cancelled/withdrawn"
    },
    "columns": {
      "name": "Team name",
      "code": "Team code",
      "status": "Status",
      "category": "Category",
      "leader": "Leader",
      "members": "Members",
      "registration": "Registration",
      "eligibilitySubmitted": "Eligibility submitted",
      "eligibilityApproved": "Eligibility approved",
      "submission": "Submission",
      "judging": "Judging",
      "attention": "Attention",
      "updatedAt": "Updated at"
    },
    "registration": {
      "complete": "Complete",
      "incomplete": "Incomplete"
    },
    "submissionState": {
      "missing": "Not submitted",
      "draft": "Draft",
      "submitted": "Submitted"
    },
    "attention": {
      "registrationIncomplete": "Registration incomplete",
      "eligibilityMissing": "Eligibility proof not submitted",
      "eligibilityRejected": "Eligibility proof rejected",
      "eligibilityExpired": "Eligibility proof expired",
      "submissionMissing": "Submission missing",
      "noJudgeAssignment": "No judge assigned"
    },
    "memberExpand": {
      "count": "{{count}} members"
    },
    "reminders": {
      "cta": "Send Registration Reminder",
      "dialogTitle": "Send Registration Reminder",
      "dialogTarget": "Every team leader whose team has not completed registration",
      "dialogScope": "This competition",
      "dialogImpact": "Sends one reminder email to each team leader whose team has not yet completed registration, asking them to finish soon. Teams that already completed registration or have been cancelled/withdrawn will not receive an email.",
      "dialogConfirm": "Send reminder",
      "dialogSuccess": "Sent a reminder to {{count}} team(s).",
      "previewTitle": "Email preview",
      "previewLoading": "Loading preview…",
      "previewSample": "Based on team \"{{name}}\"; each leader's email names their own team. This is the plain-text content; the real email also has HTML layout.",
      "previewError": "Could not load the email preview."
    },
    "pagination": {
      "previous": "Previous",
      "next": "Next",
      "pageInfo": "{{from}}–{{to}} of {{total}}"
    },
    "detail": {
      "title": "Team detail",
      "back": "Back to teams",
      "loading": "Loading…",
      "notFound": "This team could not be found."
    },
    "members": {
      "title": "Members",
      "remove": "Remove",
      "removing": "Removing…",
      "removed": "Member removed.",
      "removeFailed": "Could not remove member.",
      "removeConfirmTitle": "Remove this member?",
      "removeScope": "Takes effect immediately.",
      "removeImpact": "Once removed, {{name}} will no longer be a member of this team. Rejoining requires accepting a new, valid invitation.",
      "removeSuccess": "Removed {{name}}.",
      "transferLeadership": "Transfer leadership",
      "newLeader": "New leader",
      "selectMember": "Choose a member…",
      "reason": "Reason",
      "transferSubmit": "Transfer leadership",
      "transferring": "Transferring…",
      "transferred": "Leadership transferred.",
      "transferFailed": "Could not transfer leadership."
    },
    "invitations": {
      "title": "Invite links",
      "description": "v1 manual flow: generate a one-time invite link and hand it to the incoming member; there is no direct-add endpoint.",
      "createOneTime": "Create one-time invite",
      "creating": "Creating…",
      "linkReady": "Invite link created:",
      "copy": "Copy invite link",
      "copyAria": "Copy invite link",
      "copied": "✓ Copied",
      "copyFailed": "Couldn't copy the invite link automatically. Please copy it manually.",
      "createFailed": "Could not create invite.",
      "show": "Show invites",
      "hide": "Hide invites",
      "empty": "No invites yet.",
      "expires": "expires",
      "revoke": "Revoke"
    }
  },
  "eligibilityReviews": {
    "title": "Eligibility Review",
    "description": "Review eligibility proofs submitted by participants. Shares the same data as Eligibility Data, focused on pending review work.",
    "loading": "Loading…",
    "forbidden": "You do not have permission to review eligibility."
  },
  "people": {
    "title": "People & roles",
    "description": "Roles apply to this competition only. The same account can hold a different role in another one.",
    "loading": "Loading…",
    "forbidden": "You do not have permission to see who holds roles here.",
    "empty": "No role grants yet.",
    "noMatches": "No members match the current filters.",
    "grantCta": "+ Grant a role",
    "summary": {
      "total": "{{count}} members",
      "roleCount": "{{count}} {{label}}"
    },
    "list": {
      "searchLabel": "Search members",
      "searchPlaceholder": "Search by name or email…",
      "roleFilterLabel": "Role (multi-select — find members who hold several at once)",
      "roleFilterAll": "All",
      "columns": {
        "member": "Member",
        "email": "Email",
        "roles": "Roles",
        "actions": "Actions"
      },
      "manageRoles": "Manage roles"
    },
    "revoke": "Revoke",
    "revokeFailed": "Could not revoke.",
    "grant": {
      "title": "Grant a role",
      "description": "By email address. The account must have signed in at least once.",
      "email": "Email",
      "existingRoles": "Current roles: {{roles}}",
      "role": "Role",
      "failed": "Could not grant the role.",
      "submit": "Grant role",
      "granted": "Role granted.",
      "cancel": "Cancel"
    },
    "manage": {
      "title": "Manage roles",
      "revoke": "Revoke",
      "revokeConfirm": "Revoke the \"{{role}}\" role?",
      "confirmRevoke": "Confirm revoke",
      "cancel": "Cancel",
      "close": "Close"
    },
    "tabs": {
      "label": "People tabs",
      "members": "Member list",
      "eligibility": "Eligibility review"
    }
  },
  "audit": {
    "title": "Audit log",
    "description": "The competition's key settings and activity history. Entries cannot be edited or removed.",
    "search": {
      "label": "Search entries",
      "placeholder": "Search by name, email, submission, team, or action…"
    },
    "categoryFilter": {
      "label": "Event type",
      "all": "All",
      "competitionSetup": "Competition setup",
      "peopleAndRoles": "People & roles",
      "team": "Teams",
      "submission": "Submissions",
      "file": "Files",
      "judging": "Judging",
      "scoring": "Scoring",
      "result": "Results",
      "form": "Forms"
    },
    "dateFilter": {
      "label": "Date range",
      "today": "Today",
      "last7Days": "Last 7 days",
      "last30Days": "Last 30 days",
      "custom": "Custom",
      "from": "From",
      "to": "To"
    },
    "advanced": {
      "toggle": "Advanced: event code",
      "placeholder": "Event code, e.g. form_version.publish"
    },
    "loading": "Loading…",
    "loadingMore": "Loading more…",
    "loadMore": "Load more",
    "forbidden": "You do not have permission to read this competition's audit log.",
    "empty": "No entries match.",
    "deletedAccount": "A deleted account",
    "rawEventCode": "Event code",
    "target": "Target",
    "reason": "Reason: {{reason}}",
    "viewChanges": "View changes",
    "diff": {
      "field": "Field",
      "before": "Before",
      "after": "After",
      "empty": "This entry has no comparable field data.",
      "viewRaw": "View raw data"
    }
  },
  "submissions": {
    "title": "Submissions",
    "description": "Every submission across teams in this competition.",
    "filters": {
      "statusPlaceholder": "All statuses",
      "categoryPlaceholder": "All categories",
      "teamPlaceholder": "Team ID",
      "roundPlaceholder": "All rounds"
    },
    "loading": "Loading…",
    "forbidden": "You do not have permission to see submissions across teams.",
    "empty": "No submissions match.",
    "notSubmitted": "Not submitted",
    "columns": {
      "team": "Team",
      "category": "Category",
      "status": "Status",
      "submittedAt": "Submitted",
      "round": "Round"
    },
    "pagination": {
      "previous": "Previous",
      "next": "Next",
      "pageInfo": "{{from}}–{{to}} of {{total}}"
    },
    "detail": {
      "title": "Submission detail",
      "back": "Back to submissions",
      "loading": "Loading…",
      "notFound": "This submission could not be found.",
      "team": "Team",
      "files": {
        "title": "Uploaded files",
        "empty": "No files uploaded yet.",
        "uploadedBy": "Uploaded by {{name}}"
      },
      "answers": "Answers",
      "noAnswers": "No answers recorded.",
      "versions": {
        "title": "Submitted versions",
        "description": "Immutable snapshots captured at each submit. A version's fields never change after it is created.",
        "empty": "No submitted version yet.",
        "version": "Version {{number}}"
      },
      "scores": {
        "matrix": {
          "title": "Per-criterion comparison",
          "criterion": "Criterion",
          "gap": "Gap",
          "gapValue": "Gap {{gap}}",
          "weight": "({{weight}}%)"
        },
        "spread": {
          "title": "Judges' scores differ widely.",
          "detail": "Highest {{highest}}, lowest {{lowest}}, a gap of {{spread}} points. Before calculating results, check for a scoring mistake; you can ask a judge to review it again (reopen)."
        },
        "title": "Judge scores",
        "description": "Shows each judge's review comment and total score only — a judge's personal private note stays invisible to organizers.",
        "loading": "Loading scores…",
        "empty": "No judge has scored this submission yet.",
        "statusBadge": {
          "draft": "Draft",
          "submitted": "Submitted",
          "locked": "Submitted",
          "reopened": "Reopened"
        },
        "totalScore": "Total {{total}} / 100",
        "totalScorePending": "Total not yet calculated",
        "reviewComment": "Review comment",
        "reviewCommentEmpty": "(not filled in)",
        "reopen": {
          "action": "Reopen",
          "dialogTitle": "Reopen score",
          "dialogScope": "This judge's score on this submission only",
          "dialogImpact": "The judge will be able to revise and resubmit their score. The original score and this reason are kept in the audit trail.",
          "dialogConfirm": "Reopen score",
          "dialogSuccess": "Score reopened. The judge can now revise and resubmit it."
        }
      },
      "actions": {
        "title": "Submission actions",
        "lock": {
          "action": "Lock",
          "dialogTitle": "Lock submission",
          "dialogImpact": "The submission's status will change to \"Locked\". This action and reason are kept in the audit trail.",
          "dialogConfirm": "Lock submission",
          "dialogSuccess": "Submission locked. An organizer can unlock it again if needed."
        },
        "unlock": {
          "action": "Unlock",
          "dialogTitle": "Unlock submission",
          "dialogImpact": "The submission's status will change to \"Ready\", so the team can revise and formally resubmit it. This action and reason are kept in the audit trail.",
          "dialogConfirm": "Unlock submission",
          "dialogSuccess": "Submission unlocked. The team can now revise and resubmit it."
        },
        "withdraw": {
          "action": "Withdraw",
          "dialogTitle": "Withdraw submission",
          "dialogImpact": "The submission's status will change to \"Withdrawn\". This action and reason are kept in the audit trail.",
          "dialogConfirm": "Withdraw submission",
          "dialogSuccess": "Submission withdrawn."
        },
        "disqualify": {
          "action": "Disqualify",
          "dialogTitle": "Disqualify submission",
          "dialogImpact": "The submission becomes \"Disqualified\"; the whole team can no longer submit or edit in any round, result calculation excludes it, and its unsubmitted judge assignments are cancelled automatically (submitted scores are kept); the team is notified by email (without the reason). The action and reason are kept in the audit trail, and a competition admin can reinstate it.",
          "dialogConfirm": "Disqualify",
          "dialogSuccess": "Submission disqualified."
        },
        "returnForCorrection": {
          "action": "Return for correction",
          "dialogTitle": "Return submission for correction",
          "dialogImpact": "The submission's status will change to \"Ready\" and the team will receive an email explaining what needs to change, so they can revise and formally resubmit it. This action and reason are kept in the audit trail.",
          "dialogConfirm": "Return for correction",
          "dialogSuccess": "Submission returned for correction. The team will receive an email explaining what needs to change."
        },
        "dialogScope": "This submission only",
        "reinstate": {
          "action": "Reinstate",
          "dialogTitle": "Reinstate a disqualified team",
          "dialogImpact": "The submission returns to the status it had before disqualification (submitted or locked, never draft) and the team may continue; it is notified by email (without the reason). Results are not recalculated and cancelled judge assignments are not restored; the reason and action are kept in the audit trail.",
          "dialogConfirm": "Reinstate",
          "dialogSuccess": "The disqualification was reversed.",
          "recalculateNote": "This round already has calculated results: recalculate them on the Results page, and reassign any cancelled judge assignments separately."
        }
      },
      "round": "Round: {{name}}"
    }
  },
  "files": {
    "title": "Files",
    "description": "Every uploaded file across submissions in this competition.",
    "filters": {
      "statusPlaceholder": "All statuses",
      "submissionPlaceholder": "Submission ID"
    },
    "loading": "Loading…",
    "forbidden": "You do not have permission to see files across teams.",
    "empty": "No files match.",
    "columns": {
      "filename": "File",
      "team": "Team",
      "type": "Type",
      "size": "Size",
      "status": "Status",
      "actions": "Actions"
    },
    "pagination": {
      "previous": "Previous",
      "next": "Next",
      "pageInfo": "{{from}}–{{to}} of {{total}}"
    }
  },
  "overview": {
    "loading": "Loading…",
    "loadFailed": "Could not load the competition.",
    "saved": "Saved.",
    "operationsCta": {
      "description": "Want to see how the competition is running right now (registration, eligibility, submissions, judging progress)?",
      "label": "Go to Operations Dashboard →"
    },
    "moreActions": {
      "label": "More actions",
      "export": "Export competition configuration",
      "clone": "Clone competition",
      "saveAsTemplate": "Save as template"
    },
    "basic": {
      "title": "Competition basic information",
      "slug": "Platform URL",
      "name": "Name",
      "officialWebsiteUrl": "Official competition website",
      "shortDescription": "Short description",
      "saveFailed": "Could not save.",
      "saving": "Saving…",
      "save": "Save changes",
      "cancel": "Cancel",
      "year": "Year",
      "timezone": "Timezone",
      "notSet": "Not set",
      "editTitle": "Edit competition basic information",
      "platformPath": "Platform internal competition path:",
      "openWebsite": "Open website ↗",
      "slugChangeWarning": "Changing the platform URL may break the competition's previous link.",
      "slugConflict": "This platform URL is already used by another competition.",
      "slugInvalid": "Invalid platform URL — use lowercase letters, numbers and hyphens only.",
      "nameRequired": "Please enter a name.",
      "officialWebsiteUrlInvalid": "Please enter a valid https URL.",
      "yearInvalid": "Please enter a valid year.",
      "savedMessage": "Competition basic information updated",
      "logo": {
        "label": "Competition Logo",
        "notSet": "No logo set",
        "noLogo": "None",
        "altWithName": "{{name}} logo",
        "previewAlt": "Logo preview",
        "upload": "Upload logo",
        "replace": "Replace logo",
        "remove": "Remove",
        "hint": "Square image recommended — PNG, JPG or WebP, at least 256×256 px, 2 MB max.",
        "aspectWarning": "A square image is recommended; this image isn't 1:1 and may be cropped.",
        "uploading": "Uploading… ({{percent}}%)",
        "finalizing": "Processing…",
        "errors": {
          "fileType": "Only PNG, JPG or WebP images are accepted.",
          "tooLarge": "The file must be 2 MB or smaller.",
          "tooSmall": "The image must be at least {{min}}×{{min}} px.",
          "unreadable": "Could not read this image file. Please confirm it isn't corrupted and try again.",
          "generic": "Could not upload the logo. Please try again.",
          "genericWithDetail": "Could not upload the logo: {{detail}}"
        }
      }
    },
    "schedule": {
      "title": "Schedule",
      "registrationStart": "Registration opens",
      "registrationEnd": "Registration closes",
      "submissionStart": "Submissions open",
      "submissionEnd": "Submissions close",
      "judgingStart": "Judging opens",
      "judgingEnd": "Judging closes",
      "resultPublish": "Results publish",
      "saveFailed": "Could not save the schedule.",
      "saving": "Saving…",
      "save": "Save schedule",
      "timezoneNotice": "All competition times are shown in {{timezone}}.",
      "utcNotice": "Stored internally as UTC.",
      "editCta": "Edit schedule",
      "editTitle": "Edit schedule",
      "notSet": "Not set",
      "orderingWarning": "Submissions close after judging opens — check the ordering.",
      "orderingWarningRound": "{{roundName}}: submissions close after judging opens — check the ordering.",
      "crossRoundWarning": "{{toRound}} starts submissions/registration before {{fromRound}} publishes results — check the round order.",
      "manageRoundsCta": "Manage round schedules",
      "multiRoundNotice": "This competition uses multiple rounds. Manage each stage's timing under \"Rounds\".",
      "goToRounds": "Fix it →",
      "phases": {
        "registration": "Registration",
        "submission": "Submissions",
        "judging": "Judging",
        "resultPublish": "Results"
      }
    },
    "eligibility": {
      "title": "Eligibility",
      "description": "Team size and participation rules.",
      "minTeamSize": "Minimum team size",
      "maxTeamSize": "Maximum team size",
      "allowTeacher": "Teams may include a teacher",
      "requireTeacher": "A teacher is required",
      "allowMultipleSubmissions": "Teams may submit more than once",
      "anonymousJudging": "Anonymous judging",
      "saveFailed": "Could not save eligibility rules.",
      "saving": "Saving…",
      "save": "Save eligibility",
      "editTitle": "Edit eligibility rules",
      "groups": {
        "team": "Team rules",
        "submission": "Submission rules",
        "judging": "Judging rules"
      },
      "yes": "Yes",
      "no": "No",
      "on": "On",
      "off": "Off"
    },
    "readiness": {
      "status": {
        "ready": "ready",
        "pending": "not started",
        "unavailable": "not available on this platform"
      },
      "items": {
        "basicInformation": "Basic information",
        "schedule": "Schedule",
        "eligibility": "Eligibility",
        "categories": "Categories",
        "dynamicForm": "Dynamic form",
        "rubric": "Rubric",
        "judgingSetup": "Judging setup",
        "publicPage": "Public page",
        "previewAndPublish": "Publish"
      },
      "actionNeeded": {
        "title": "Action needed",
        "description": "Only settings that still need attention are listed here.",
        "allClear": "Nothing needs attention right now."
      },
      "collapsedSummary": "Readiness {{ready}} / {{total}} complete"
    },
    "status": {
      "title": "Status",
      "pinnedStatus": "Pinned status",
      "followSchedule": "Follow the schedule",
      "reason": "Reason (required)",
      "applyFailed": "Could not change the status.",
      "riskNotice": "Overriding the status changes what participants see across the whole competition. Review the change before applying it.",
      "scheduleImplies": "Schedule currently implies",
      "actualStatus": "Actual status",
      "overrideActive": "Overridden by an administrator",
      "review": "Review change",
      "cancelOverride": "Cancel override",
      "confirmMessage": "Manually switch the competition from \"{{from}}\" to \"{{to}}\".",
      "confirmDetail": "The schedule keeps being computed, but no longer decides the actual status. Confirm this matches where the competition actually is.",
      "confirmApply": "Confirm and apply",
      "applying": "Applying…"
    },
    "settings": {
      "title": "Settings",
      "description": "Public contact details and page visibility.",
      "contactEmail": "Contact email",
      "publicPageLive": "Public competition page is live",
      "reviewCommentRequired": "Judges must fill in a review comment before submitting a score",
      "saveFailed": "Could not save the settings.",
      "save": "Save settings",
      "editTitle": "Edit settings",
      "notSet": "Not set",
      "live": "Live",
      "offline": "Offline",
      "saving": "Saving…",
      "eligibilityText": "Eligibility",
      "submissionRequirementsText": "Submission Requirements",
      "judgingCriteriaText": "Judging Criteria",
      "prizesText": "Prizes",
      "organizerContactText": "Organizer Contact",
      "faq": {
        "title": "FAQ",
        "question": "Question",
        "answer": "Answer",
        "remove": "Remove",
        "add": "Add FAQ entry"
      }
    },
    "discoveryCategories": {
      "title": "Discovery categories",
      "description": "Which public discovery filters this competition should show up under. Separate from this competition's own tracks (the \"Categories\" tab below) — they don't affect each other.",
      "saveFailed": "Could not save the categories.",
      "save": "Save categories",
      "none": "No categories selected.",
      "add": "+ Add category"
    },
    "milestone": {
      "title": "Next milestone",
      "roundPhase": "{{roundName}} · {{phase}}",
      "daysUntil_one": "In {{count}} day",
      "daysUntil_other": "In {{count}} days"
    },
    "studentEligibilityPolicy": {
      "title": "Student eligibility verification",
      "description": "Whether this competition requires participating students to verify current enrollment, and the review rules.",
      "editTitle": "Edit student eligibility verification settings",
      "disabledSummaryTitle": "Currently not enabled",
      "disabledSummaryBody": "Participating students are not currently required to provide proof of enrollment.",
      "enabledStatus": "Enabled",
      "scopeSummaryLabel": "Applies to",
      "proofTypesSummaryLabel": "Accepted proof",
      "reviewSummaryLabel": "Review",
      "reviewManual": "Manual review",
      "reviewNotRequired": "No review required",
      "pendingSummaryLabel": "While pending",
      "pendingAllowed": "Registration may be submitted while pending",
      "pendingNotAllowed": "Registration must wait until review is approved",
      "validitySummaryLabel": "Proof validity",
      "validityDaysSummary": "Valid for {{days}} days after approval",
      "enabledToggle": "Enable student eligibility verification",
      "enabledToggleHint": "Once enabled, participating students must provide proof of enrollment per the settings below.",
      "scope": "Requirement scope",
      "scopeOptions": {
        "all_student_members": "All student members",
        "all_participating_members": "All participating members (excluding teachers)",
        "team_leader_only": "Team leader only",
        "minimum_verified_members": "At least N members"
      },
      "scopeNotSet": "Not selected",
      "minimumVerifiedMembers": "Minimum verified members",
      "minimumVerifiedMembersUnit": "members",
      "proofTypesSectionTitle": "Accepted proof documents",
      "proofTypeOptions": {
        "enrollment_certificate": "Enrollment certificate",
        "student_id": "Student ID",
        "other_approved": "Other approved document"
      },
      "proofTypesRequiredError": "Select at least one accepted proof document.",
      "reviewSectionTitle": "Review",
      "reviewRequired": "Manual review required",
      "allowSubmitWhilePending": "Allow registration submit while pending",
      "validitySectionTitle": "Proof validity",
      "validityUnlimitedOption": "No expiry",
      "validityLimitedOption": "Valid for",
      "validityDaysUnit": "days after approval",
      "proofValidityDays": "Proof validity (days)",
      "unlimitedDays": "No limit",
      "notSet": "Not set",
      "cancel": "Cancel",
      "save": "Save changes",
      "saving": "Saving…",
      "saveFailed": "Could not save — please try again.",
      "loadFailed": "Could not load student eligibility settings."
    }
  },
  "builder": {
    "title": "New competition",
    "intro": "This creates the competition as a draft; nothing is visible to participants until its schedule is set. Schedule, eligibility and everything else are configured from the competition's Overview tab after creation.",
    "formCard": {
      "title": "Basic information",
      "description": "You can change any of this later from the competition's Overview tab."
    },
    "fields": {
      "name": "Name",
      "slug": "Slug",
      "slugHint": "Lowercase words separated by hyphens. Unique across the platform.",
      "year": "Year",
      "timezone": "Display time zone",
      "shortDescription": "Short description (optional)"
    },
    "createFailed": "Could not create the competition.",
    "creating": "Creating…",
    "create": "Create competition"
  },
  "dateStrategy": {
    "title": "Date strategy",
    "clear": "Clear",
    "clearHint": "Clear every date - set a new schedule after creation.",
    "preserve": "Preserve",
    "preserveHint": "Keep the original dates; if any date has already passed, a warning shows after submitting.",
    "shift": "Shift",
    "shiftHint": "Shift the entire schedule to a new point in time, anchored to the date below.",
    "anchorDate": "Anchor date",
    "anchorDateHint": "Every date shifts relative to this one - the order between dates never changes.",
    "warningsTitle": "Warnings",
    "continue": "Continue"
  },
  "clone": {
    "title": "Clone competition",
    "intro": "Copy configuration from \"{{name}}\" and submit a new competition request.",
    "formCard": {
      "title": "New competition details",
      "description": "The cloned competition is a fresh draft — no participants, teams, submissions, judges, or scores are copied. A platform admin must approve the request before the competition is actually created, the same as any other new competition."
    },
    "fields": {
      "name": "Name",
      "slug": "Slug",
      "year": "Year"
    },
    "options": {
      "title": "Clone options",
      "includePublicPageText": "Include public page text",
      "includeJudgingSettings": "Include judging settings (rubrics)",
      "dateStrategyNote": "All dates will be cleared — set a new schedule after cloning."
    },
    "excludedNotice": "Never copied: participants, teams, submissions, judge assignments, scores, results, audit history.",
    "failed": "Could not submit the request — please try again.",
    "submitting": "Submitting…",
    "submit": "Submit request"
  },
  "saveAsTemplate": {
    "title": "Save as template",
    "intro": "Save the current configuration of \"{{name}}\" as a reusable template.",
    "formCard": {
      "title": "Template details",
      "description": "A template holds configuration only — no participants, teams, submissions, judges, or scores."
    },
    "fields": {
      "name": "Template name",
      "description": "Description (optional)",
      "visibility": "Visibility"
    },
    "visibility": {
      "private": "Private (only me)",
      "platform": "Platform-shared (every organizer)"
    },
    "success": "Template saved.",
    "failed": "Could not save the template — please try again.",
    "submitting": "Saving…",
    "submit": "Save as template"
  },
  "createFromTemplate": {
    "title": "Create competition from template",
    "intro": "Pick a template to create a new draft competition.",
    "loading": "Loading…",
    "loadError": "Could not load templates.",
    "empty": "No templates are available yet.",
    "visibilityLabel": "Visibility: {{visibility}}",
    "formCard": {
      "title": "New competition details",
      "description": "The competition created from this template is a fresh draft — all dates will be cleared. A platform admin must approve the request before the competition is actually created, the same as any other new competition."
    },
    "fields": {
      "name": "Name",
      "slug": "Slug",
      "year": "Year"
    },
    "failed": "Could not submit the request — please try again.",
    "submitting": "Submitting…",
    "submit": "Submit request"
  },
  "import": {
    "title": "Import competition from JSON",
    "intro": "Paste the contents of a previously exported .napaicp.json file and submit a new competition request.",
    "fileCard": {
      "title": "Competition configuration JSON",
      "description": "Plain JSON text only — no archives or executable files."
    },
    "fields": {
      "fileContents": "JSON contents",
      "fileContentsHint": "Paste the full contents of a .napaicp.json file.",
      "name": "Name",
      "slug": "Slug",
      "year": "Year"
    },
    "validate": "Validate",
    "validating": "Validating…",
    "validateFailed": "Validation failed — please try again.",
    "preflight": {
      "title": "Validation result",
      "sourceName": "Source competition name",
      "schemaVersion": "Schema version",
      "status": "Status",
      "ready": "Ready to create",
      "blocked": "Cannot create",
      "unknown": "Unknown",
      "warningsTitle": "Warnings",
      "blockingTitle": "Blocking errors"
    },
    "excludedNotice": "Never imported: participants, teams, submissions, judge assignments, scores, results, audit history, or any password/token field. A platform admin must approve the request before the competition is actually created, the same as any other new competition.",
    "create": "Submit request",
    "creating": "Submitting…",
    "createFailed": "Could not submit the request — please try again."
  },
  "formEditor": {
    "notFound": {
      "title": "Form not found",
      "description": "It does not exist, or you hold no role in this competition."
    },
    "loading": "Loading…",
    "versionLive": "v{{version}} live",
    "draftPending": "draft pending",
    "publishing": "Publishing…",
    "publish": "Publish",
    "viewingPublished": "You are viewing the published version. Editing a field clones a new draft version instead of changing what participants currently see.",
    "draftNotice": "Editing v{{draft}} draft — participants still see v{{live}}.",
    "draftNoticeUnpublished": "Editing v{{draft}} draft — this form has not been published yet, so participants do not see it.",
    "preview": "Preview form",
    "previewTitle": "Participant preview",
    "previewDescription": "Fields exactly as configured on the version you're currently editing. Try answering to check conditional fields; nothing is submitted or saved.",
    "previewEmpty": "No fields to preview yet.",
    "tabs": {
      "fields": "Field design",
      "settings": "Form settings",
      "versions": "Version history"
    },
    "settings": {
      "title": "Form settings",
      "description": "Display name and description shown to admins picking this form for a round.",
      "name": "Name",
      "descriptionLabel": "Description (optional)",
      "save": "Save",
      "saving": "Saving…",
      "saveFailed": "Could not save form settings."
    },
    "fields": {
      "title": "Fields",
      "description": "Drag to reorder. Each field's editor sets its type, requiredness, validation and conditional visibility.",
      "loading": "Loading…",
      "empty": "No fields yet.",
      "addField": "Add field",
      "reorder": "Reorder {{label}}",
      "moveUp": "Move {{label}} up",
      "moveDown": "Move {{label}} down",
      "reorderFailed": "Couldn't save the new order. Reverted to the previous order — please try again.",
      "required": "required",
      "conditional": "conditional",
      "keyLabel": "key",
      "edit": "Edit",
      "delete": "Delete",
      "editTitle": "Edit field",
      "addTitle": "Add field",
      "advanced": "Advanced: key `{{key}}`, type `{{type}}`",
      "deleteConfirmTitle": "Confirm field deletion",
      "deleteScope": "This form's current draft version",
      "deleteImpact": "The field will be removed from the form. Answers already collected stay on past submissions' history, but will no longer appear on new versions of the form.",
      "deleteSuccess": "Field deleted.",
      "deleteFailed": "Could not delete the field."
    },
    "versions": {
      "title": "Version history",
      "description": "A published version is never edited in place; every change after publish lands on a new version, so a submission bound to an old one always redisplays correctly.",
      "empty": "No versions yet.",
      "version": "Version {{number}}"
    },
    "form": {
      "key": "Key",
      "type": "Type",
      "label": "Label",
      "description": "Description (optional)",
      "required": "Required",
      "placeholder": "Placeholder (optional)",
      "urlSuggestion": {
        "title": "Suggested field description (URL fields)",
        "text": "Please provide a link judges can open directly. For video use YouTube (unlisted) or Vimeo; for cloud storage such as Google Drive, set sharing to \"Anyone with the link can view\" and do not edit or revoke it until judging ends. For anonymous judging, do not show team names, school names, or other identifying information in the content or the sharing account.",
        "apply": "Apply suggested text"
      },
      "sectionLabel": "Section (optional)",
      "sectionLabelPlaceholder": "e.g. Proposal Slides",
      "sectionLabelHint": "Fields sharing the exact same section name are grouped under one heading in the participant's Submission Workspace. Leave blank to keep this field on its own.",
      "minLength": "Min length",
      "maxLength": "Max length",
      "pattern": "Pattern (regex, optional)",
      "min": "Min",
      "max": "Max",
      "allowedExtensions": "Allowed extensions (comma separated)",
      "fileUploadUnavailable": "File upload is not available yet; this only configures the field.",
      "saving": "Saving…",
      "saveField": "Save field",
      "cancel": "Cancel"
    },
    "options": {
      "title": "Options",
      "value": "value",
      "label": "label",
      "remove": "Remove",
      "add": "Add option"
    },
    "visibility": {
      "toggle": "Only show this field conditionally",
      "matchAll": "Match all conditions",
      "matchAny": "Match any condition",
      "fieldPlaceholder": "Field…",
      "equals": "equals",
      "notEquals": "not equals",
      "valuePlaceholder": "value (true, false, or text)",
      "remove": "Remove",
      "addCondition": "Add condition"
    }
  },
  "eligibilityReview": {
    "title": "Eligibility review",
    "description": "Review participating students' enrollment proofs — approve or reject.",
    "loading": "Loading…",
    "forbidden": "You do not have permission to view eligibility review.",
    "empty": "No members currently need review.",
    "noMatches": "No members match the current filters.",
    "review": "Review",
    "summary": {
      "verified": "Verified",
      "pending": "Pending",
      "rejected": "Rejected",
      "missingNote": "\"Missing\" cannot be counted without matching every team's required-member list — the eligibility API does not provide that aggregate yet, so it is not shown here."
    },
    "filters": {
      "statusPlaceholder": "All statuses",
      "teamPlaceholder": "Filter by team ID",
      "searchPlaceholder": "Search name, email, or school…"
    },
    "columns": {
      "member": "Member",
      "team": "Team",
      "school": "School",
      "proof": "Proof",
      "status": "Status",
      "updatedAt": "Updated",
      "actions": "Actions"
    },
    "proof": {
      "submitted": "Submitted",
      "notSubmitted": "Not submitted"
    },
    "pagination": {
      "pageInfo": "Showing {{from}}–{{to}} of {{total}}",
      "previous": "Previous",
      "next": "Next"
    },
    "drawer": {
      "noTeam": "No team yet",
      "notSet": "Not provided",
      "school": "School",
      "department": "Department",
      "proofTitle": "Enrollment proof",
      "noProof": "No enrollment proof uploaded yet.",
      "decisionLabel": "Decision",
      "decisionVerified": "Verified",
      "decisionRejected": "Rejected",
      "reasonLabel": "Reason (required when rejecting)",
      "rejectNotice": "The system will email the student with the reason you enter, so please word it carefully.",
      "reasonRequired": "A reason is required to reject.",
      "submitting": "Submitting…",
      "confirm": "Confirm review",
      "success": "Review submitted.",
      "failed": "Could not submit the review — please try again."
    }
  },
  "participants": {
    "title": "Participants",
    "description": "Every team leader and member competing in this competition — separate from staff/admin/judge roles (see People & roles).",
    "loading": "Loading…",
    "loadFailed": "Could not load participants.",
    "empty": "No participants yet.",
    "noMatches": "No participants match the current filters.",
    "notSet": "Not provided",
    "summary": {
      "total": "Participants",
      "teams": "Teams",
      "leaders": "Team leaders",
      "members": "Team members",
      "verified": "Verified",
      "pending": "Pending review",
      "rejected": "Rejected",
      "missing": "Not submitted"
    },
    "filters": {
      "searchPlaceholder": "Search name, email, school…",
      "teamPlaceholder": "All teams",
      "categoryPlaceholder": "All categories",
      "eligibilityPlaceholder": "All eligibility statuses",
      "registrationPlaceholder": "All registration statuses",
      "sortPlaceholder": "Sort",
      "sortDefault": "Sort: Name (default)",
      "sortName": "Sort: Name",
      "sortUpdatedAt": "Sort: Last activity"
    },
    "columns": {
      "name": "Name",
      "team": "Team",
      "teamRole": "Team role",
      "category": "Category",
      "school": "School",
      "department": "Department",
      "eligibility": "Eligibility",
      "registration": "Registration",
      "updatedAt": "Last activity"
    },
    "teamRole": {
      "leader": "Leader",
      "member": "Member"
    },
    "registrationStatus": {
      "complete": "Complete",
      "incomplete": "Incomplete"
    },
    "pagination": {
      "pageInfo": "{{from}}–{{to}} of {{total}}",
      "previous": "Previous",
      "next": "Next"
    }
  },
  "registrationData": {
    "title": "Registration Data",
    "description": "Every team's registration-form completion, read-only. Completion is never affected by eligibility verification status.",
    "needsUpdate": {
      "title": "Registrations that need an update",
      "description": "{{count}} student(s) submitted a registration whose saved answers no longer satisfy the current form.",
      "noTeam": "No team",
      "missing": "Still to fill in: ",
      "cta": "Send update reminder",
      "dialogTitle": "Send registration update reminder",
      "dialogTarget": "{{count}} student(s) whose registration needs an update",
      "dialogScope": "This competition",
      "dialogImpact": "The system emails each of these students themself, naming the fields still to fill in with a link to do so. Team leaders and teachers are not emailed, and no registration data is filled in or changed for anyone.",
      "dialogConfirm": "Send reminder",
      "dialogSuccess": "Update reminder sent to {{count}} student(s).",
      "testCta": "Send a test to myself",
      "testSent": "A test email was sent to your account address. No student was emailed.",
      "testError": "Could not send the test email. Please try again.",
      "previewTitle": "Email preview",
      "previewLoading": "Loading preview…",
      "previewSample": "Based on \"{{name}}\"; each student's email lists the fields they are missing.",
      "previewError": "Could not load the email preview."
    },
    "loading": "Loading…",
    "forbidden": "You do not have permission to view registration data.",
    "empty": "No teams yet.",
    "noMatches": "No teams match the current filters.",
    "filters": {
      "teamPlaceholder": "Filter by team ID",
      "completedPlaceholder": "All completion statuses",
      "completedTrue": "Completed",
      "completedFalse": "Incomplete",
      "searchPlaceholder": "Search by team name, leader name, or email…"
    },
    "columns": {
      "team": "Team",
      "leader": "Leader",
      "formVersion": "Form version",
      "completed": "Completion status",
      "updatedAt": "Last modified",
      "actions": "Actions"
    },
    "completed": {
      "true": "Completed",
      "false": "Incomplete"
    },
    "noVersion": "No active registration form",
    "viewAnswers": "View answers",
    "pagination": {
      "pageInfo": "{{from}}–{{to}} of {{total}}",
      "previous": "Previous",
      "next": "Next"
    },
    "detail": {
      "title": "Registration form answers",
      "loading": "Loading…",
      "notFound": "This team has not answered the active registration form yet.",
      "failed": "Could not load the answers.",
      "formVersion": "Form version v{{version}}",
      "submittedAt": "First submitted: {{time}}",
      "updatedAt": "Last modified: {{time}}",
      "close": "Close"
    }
  },
  "eligibilityData": {
    "title": "Eligibility Data",
    "description": "A read-only data view of eligibility verification, backed by the exact same data as the Review Workbench. Approve/reject actions stay on the Eligibility Review page.",
    "loading": "Loading…",
    "forbidden": "You do not have permission to view eligibility data.",
    "empty": "No eligibility records yet.",
    "noMatches": "No members match the current filters.",
    "filters": {
      "statusPlaceholder": "All statuses",
      "teamPlaceholder": "Filter by team ID",
      "searchPlaceholder": "Search by name, email, or school…"
    },
    "columns": {
      "member": "Member",
      "team": "Team",
      "school": "School",
      "proofType": "Proof type",
      "status": "Effective status",
      "currentVersion": "Current version",
      "updatedAt": "Updated at",
      "reviewer": "Reviewer",
      "rejectionReason": "Rejection reason",
      "actions": "Actions"
    },
    "noProof": "Not submitted",
    "filePurged": "File purged per retention policy",
    "notSet": "Not set",
    "reviewLink": "Go to Review Workbench",
    "history": {
      "title": "Version history",
      "empty": "No prior versions.",
      "purged": "(file purged per retention policy)"
    },
    "pagination": {
      "pageInfo": "{{from}}–{{to}} of {{total}}",
      "previous": "Previous",
      "next": "Next"
    }
  },
  "exportCenter": {
    "title": "Export Center",
    "description": "Server-generated CSV/XLSX reports for this competition, with column selection, filtered export, and a full export audit trail.",
    "columnsLabel": "Columns",
    "applyFilters": "Apply current filters",
    "scope": {
      "loading": "Calculating export count…",
      "failed": "Could not calculate the export count.",
      "willExport": "Will export {{count}} row(s)",
      "currentFilters": "Current filters: {{filters}}",
      "noFilters": "Current filters: none"
    },
    "downloadCsv": "Download CSV",
    "downloadXlsx": "Download XLSX",
    "downloading": "Downloading…",
    "reports": {
      "teams": "Teams",
      "submissions": "Submissions",
      "participants": "Participants",
      "eligibility": "Eligibility",
      "registration": "Registration",
      "judging": "Judging",
      "scores": "Scores & review comments"
    },
    "columns": {
      "teams": {
        "name": "Team name",
        "code": "Code",
        "status": "Status",
        "category": "Category",
        "leader": "Leader",
        "createdAt": "Created at"
      },
      "submissions": {
        "id": "Submission ID",
        "team": "Team",
        "status": "Status",
        "submittedAt": "Submitted at",
        "createdAt": "Created at"
      },
      "participants": {
        "name": "Name",
        "email": "Email",
        "team": "Team",
        "teamRole": "Team role",
        "category": "Category",
        "school": "School",
        "department": "Department",
        "eligibilityStatus": "Eligibility status",
        "registrationStatus": "Registration status"
      },
      "eligibility": {
        "member": "Member",
        "email": "Email",
        "team": "Team",
        "school": "School",
        "proofType": "Proof type",
        "effectiveStatus": "Effective status",
        "updatedAt": "Updated at"
      },
      "registration": {
        "team": "Team",
        "leader": "Leader",
        "formVersion": "Form version",
        "completionStatus": "Completion status",
        "lastModified": "Last modified"
      },
      "judging": {
        "judge": "Judge",
        "email": "Email",
        "invitationStatus": "Invitation status",
        "assignmentCount": "Assignment count",
        "completedCount": "Completed count"
      },
      "scores": {
        "round": "Round",
        "category": "Category",
        "team": "Team",
        "submissionId": "Submission ID",
        "judge": "Judge",
        "status": "Status",
        "totalScore": "Total score",
        "criteria": "Criterion scores",
        "reviewComment": "Review comment",
        "submittedAt": "Submitted at"
      }
    },
    "filters": {
      "status": "Status",
      "teamId": "Team ID",
      "search": "Search",
      "completed": "Completion status",
      "completedTrue": "Completed",
      "completedFalse": "Incomplete"
    },
    "errors": {
      "noColumns": "Select at least one column.",
      "failed": "The export failed."
    },
    "history": {
      "title": "Export history",
      "description": "Every export call against this competition, read-only.",
      "filterPlaceholder": "All report kinds",
      "loading": "Loading…",
      "failed": "Could not load export history.",
      "empty": "No exports yet.",
      "noFilters": "No filters applied",
      "systemActor": "System",
      "columns": {
        "time": "Time",
        "actor": "Actor",
        "reportKind": "Report",
        "filters": "Filters",
        "rowCount": "Rows"
      }
    }
  },
  "globalSearch": {
    "placeholder": "Search teams, participants, submissions, judges…",
    "loading": "Searching…",
    "error": "Search failed. Try again.",
    "noResults": "No results.",
    "groups": {
      "teams": "Teams",
      "participants": "Participants",
      "submissions": "Submissions",
      "judges": "Judges"
    },
    "status": {
      "registration": {
        "complete": "Registration complete",
        "incomplete": "Registration incomplete",
        "cancelled": "Cancelled"
      },
      "judgeAccepted": "Invitation accepted"
    }
  },
  "announcements": {
    "loading": "Loading…",
    "forbidden": "You do not have permission to manage announcements.",
    "description": "Announcements appear on the public competition page and on participants' Competition Information page. You can also email them.",
    "audience": {
      "all": "Everyone (incl. visitors)",
      "students": "Participants",
      "judges": "Judges"
    },
    "form": {
      "createTitle": "New announcement",
      "editTitle": "Edit announcement",
      "title": "Title",
      "body": "Content",
      "bodyHint": "Plain text. Line breaks are kept and web addresses become links.",
      "audience": "Shown to (website)",
      "alsoEmail": "Also notify by email",
      "preview": "Preview",
      "publish": "Publish announcement",
      "publishAndEmail": "Publish and email",
      "save": "Save changes",
      "saved": "Saved.",
      "error": "Could not save. Please try again.",
      "testEmail": "Send a test to myself",
      "testSent": "A test email was sent to your account address. Nobody else received it.",
      "testError": "Could not send the test email. Please try again."
    },
    "preview": {
      "title": "Announcement preview",
      "untitled": "(no title yet)"
    },
    "recipients": {
      "title": "Email recipients",
      "roles": {
        "student": "Students",
        "teacher": "Teachers",
        "judge": "Judges"
      },
      "narrowHint": "The options below only narrow students and teachers, never judges.",
      "categories": "By category",
      "rounds": "By round (round 1: every team; later rounds: advanced teams)",
      "answerField": "Filter by registration field",
      "answerFieldNone": "No registration field filter",
      "pickRole": "Select at least one recipient role.",
      "counting": "Counting recipients…",
      "count": "Will be sent to {{count}} recipient(s)",
      "noneToEmail": "No recipients match, so no email can be sent."
    },
    "list": {
      "title": "Published announcements",
      "empty": "No announcements yet.",
      "emailed": "Emailed to {{count}} recipient(s)",
      "notEmailed": "Not emailed",
      "edit": "Edit",
      "editAria": "Edit {{title}}",
      "email": "Send email",
      "emailAria": "Send email: {{title}}",
      "delete": "Delete",
      "deleteAria": "Delete {{title}}"
    },
    "emailDialog": {
      "title": "Publish announcement and send email",
      "existingTitle": "Email announcement",
      "target": "{{count}} recipient(s)",
      "existingTarget": "Announcement \"{{title}}\", {{count}} recipient(s)",
      "scope": "This competition",
      "impact": "The announcement title and content will be emailed to the recipients above (marked as an official competition notice). Sent email cannot be recalled.",
      "resendImpact": "This announcement was already emailed. Sending again means recipients get a duplicate.",
      "confirm": "Send email",
      "success": "Emailed {{count}} recipient(s)."
    },
    "deleteDialog": {
      "title": "Delete announcement",
      "scope": "This competition",
      "impact": "The announcement is removed from the website. Emails already sent cannot be recalled.",
      "confirm": "Delete announcement",
      "success": "Deleted."
    },
    "emailPreview": {
      "title": "Email preview",
      "loading": "Loading preview…",
      "hint": "This is the plain-text content recipients will get; the real email also has HTML layout. To change it, edit the announcement title or content.",
      "error": "Could not load the email preview."
    }
  },
  "manualScores": {
    "title": "Manual score entry",
    "description": "Enter field values for each submission in rounds that accept staff input.",
    "loading": "Loading…",
    "noPermission": "You do not have permission to enter manual scores.",
    "noRounds": "No round currently accepts staff input.",
    "round": "Round",
    "loadFailed": "Could not load the entry sheet.",
    "noFields": "This round has no manual fields yet. Ask the organizer.",
    "noSubmissions": "This round has no submissions to enter.",
    "readonly": {
      "mode_disallows_staff": "This round does not accept staff input.",
      "round_not_open": "The round must be open or in judging to accept input.",
      "results_locked": "This round's results are locked or published; entries can no longer change."
    },
    "lastUpdated": "Updated by {{name}} on {{time}}",
    "notEntered": "Not entered",
    "outOfRange": "Enter a value between {{min}} and {{max}}.",
    "saveFailed": "Could not save.",
    "saved": "Saved.",
    "save": "Save this team"
  },
  "scoreBreakdown": {
    "title": "Score detail by source",
    "sourceJudge": "Judge",
    "sourceStaff": "Staff input",
    "pending": "Pending",
    "history": "Change history ({{count}})",
    "revision": "{{time}}: {{name}} changed \"{{field}}\" from {{from}} to {{to}}",
    "none": "none"
  }
}
